package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initCracOnAKtorServiceWithHikariAndExposedPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/CracOnAKtorServiceWithHikariAndExposed.md", FrontMatterElement.Builder { addScalar("title", "CRaC on a Ktor service with HikariCP and Exposed: every socket that refuses the checkpoint"); addScalar("searchTitle", "Coordinated Restore at Checkpoint on Ktor CIO, HikariCP, Exposed and Postgres: what breaks, what a restore is worth, Zulu warp engine"); addScalar("date", "2026-09-11"); addScalar("library", "zavarnik"); addScalar("description", "A CRaC checkpoint of a real Ktor service restores in 131 ms against 2 317 and its JIT is warm. Getting there means every open socket, and HikariCP names its own. What breaks, in the order it breaks, with the logs."); }))
}

@Page("/blog/crac-on-a-ktor-service-with-hikari-and-exposed")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun CracOnAKtorServiceWithHikariAndExposedPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Project Leyden's AOT cache saves the class loading and nothing else — after the start the JIT compiles exactly what it would have compiled anyway, which is ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/leyden-aot-cache-for-a-plain-kotlin-jvm-service") {
            org.jetbrains.compose.web.dom.Text("measured")
        }
        org.jetbrains.compose.web.dom.Text(" and is the honest limit of it. ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://openjdk.org/projects/crac/") {
            org.jetbrains.compose.web.dom.Text("CRaC")
        }
        org.jetbrains.compose.web.dom.Text(" — Coordinated Restore at Checkpoint — saves the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("process")
        }
        org.jetbrains.compose.web.dom.Text(": a snapshot of a warmed-up JVM, restored in a fraction of the time with its JIT already hot. On my reference service the restore is ready in 131 ms against 2 317, and it serves its first signed-in screen in 32 ms against 118.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The price is that a JVM refuses to checkpoint while anything holds an open socket, and a service holds several kinds. What follows is every one of them, in the order they refused, on a stack nobody has written this up for: Ktor CIO, HikariCP, Exposed, Postgres and a message broker. The scripts and the logs are in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("experiments/crac-*/")
        }
        org.jetbrains.compose.web.dom.Text(" and in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/konekt") {
            org.jetbrains.compose.web.dom.Text("konekt")
        }
        org.jetbrains.compose.web.dom.Text(" under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/measurements-2026-09-11/crac/")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is-worth") }) {
        org.jetbrains.compose.web.dom.Text("What it is worth")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two services, both on Zulu 25.0.4.1 with CRaC, Docker on one Linux box, the two variants restarted in alternation — five times each on the sample, ten on konekt. First a Ktor CIO sample with no database at all, timed from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker run")
        }
        org.jetbrains.compose.web.dom.Text(" to the first ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(" on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("plain start")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("restore")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("readiness, median of 5")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("642 ms (543–953)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("50 ms")
                    }
                    org.jetbrains.compose.web.dom.Text(" (45–52)")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("first ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("POST")
                    }
                    org.jetbrains.compose.web.dom.Text(" after readiness")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("31 ms (25–44)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("12 ms")
                    }
                    org.jetbrains.compose.web.dom.Text(" (11–14)")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("JIT compilations during the first 100 requests")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 555–1 591")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("321–366")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("container memory, ready / after 100 requests")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("70 / 100 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("36 / 62 MiB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The last three rows come from a second series of five with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:+PrintCompilation")
        }
        org.jetbrains.compose.web.dom.Text(" on, whose own readiness was 639 ms against 82 — the flag costs a little at start. The third row is the one the AOT cache cannot produce: the restored process compiles about a fifth as much, because the compiled code came back with it. Then ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/konekt") {
            org.jetbrains.compose.web.dom.Text("konekt")
        }
        org.jetbrains.compose.web.dom.Text(", an eSIM operator's account backend — Ktor CIO, HikariCP with ten connections, Exposed, Postgres, a broker, 128 jars — under its own compose stand:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("plain start")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("restore")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("docker run")
                    }
                    org.jetbrains.compose.web.dom.Text(" → ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/health")
                    }
                    org.jetbrains.compose.web.dom.Text(", median of 10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2 317 ms (2 141–2 513)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("131 ms")
                    }
                    org.jetbrains.compose.web.dom.Text(" (115–151)")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("first signed-in home screen")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("118 ms (102–128)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("32 ms")
                    }
                    org.jetbrains.compose.web.dom.Text(" (27–50)")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the snapshot as an image layer")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("—")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("143 MB on this run (143–165 across the day's runs)")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Those two columns compare with each other and with nothing else: they ran without a CPU limit, while the same service's AOT-cache numbers were taken under the one core its chart gives it. And the restore is not a stunt — the probe signs in, tops up, buys a plan, confirms it, watches the order reach ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("completed")
        }
        org.jetbrains.compose.web.dom.Text(" and receives twelve realtime updates over the event stream, the same twelve a plain start receives.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-refuses-the-checkpoint-in-order") }) {
        org.jetbrains.compose.web.dom.Text("What refuses the checkpoint, in order")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The listening socket, and the selector holding it.")
        }
        org.jetbrains.compose.web.dom.Text(" With nothing configured, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jcmd <pid> JDK.checkpoint")
        }
        org.jetbrains.compose.web.dom.Text(" on a running Ktor CIO server fails and the process carries on as if nothing happened:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""CheckpointOpenSocketException: sun.nio.ch.ServerSocketChannelImpl[]
CheckpointOpenSocketException: EPoll FD 30 left open in sun.nio.ch.EPollSelectorImpl@... with registered keys
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One rule in a file-descriptor policy fixes both — the selector needs no rule of its own:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-yaml") }) { org.jetbrains.compose.web.dom.Text("""type: SOCKET
listening: true
action: reopen
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("CRaC closes the socket before the snapshot and binds the same local address again on restore. No code in the application, no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Resource")
        }
        org.jetbrains.compose.web.dom.Text(" implementation, nothing. Worth knowing because the project's own documentation on GitHub still says ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("reopen")
        }
        org.jetbrains.compose.web.dom.Text(" for sockets is not implemented and will throw after restore; on Azul's 25.0.4.1 build it is implemented and works.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The connection pool — and it names itself.")
        }
        org.jetbrains.compose.web.dom.Text(" On a service with a database the same rule leaves ten sockets standing. Turn on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Djdk.crac.collect-fd-stacktraces=true")
        }
        org.jetbrains.compose.web.dom.Text(" and the JVM says who owns each one:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""CheckpointOpenSocketException: Socket[addr=postgres/172.18.0.2,port=5432,localport=47200]
  Caused by: java.lang.Exception: This file descriptor was created by HikariPool-1:connection-adder
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That flag is the single most useful thing in this whole exercise, and it is off by default.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Closing the pool's sockets does not work.")
        }
        org.jetbrains.compose.web.dom.Text(" The obvious rule — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("action: close")
        }
        org.jetbrains.compose.web.dom.Text(" on port 5432 — is the one that fails, and it fails in a way worth understanding. The connections do close; HikariCP notices immediately (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Failed to validate connection … Unable to set network timeout")
        }
        org.jetbrains.compose.web.dom.Text("); and its ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("connection-adder")
        }
        org.jetbrains.compose.web.dom.Text(" thread opens replacements while the checkpoint is still being taken. The snapshot then dies on a socket that did not exist when it started:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""CheckpointOpenSocketException: FD fd=133 type=socket path=socket:[117055],port=0
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You cannot win a race against a pool whose whole job is to keep itself full.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Ignoring them does work — for a reason that is not general.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("action: ignore")
        }
        org.jetbrains.compose.web.dom.Text(" leaves the sockets in the snapshot. On restore the engine replaces each with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/dev/null")
        }
        org.jetbrains.compose.web.dom.Text(", and then two things happen that are properties of these particular libraries: HikariCP validates every connection before handing it out, discards all ten and opens new ones; and the broker client notices EOF and reconnects itself, saying so in the log. The service works, with ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("no change to the application")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A library that does neither would hand out a dead connection, silently. That is why the verification has to be a request that reaches the far side, not a process that started — and why this result is konekt's rather than everyone's.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Your own tooling's connections.")
        }
        org.jetbrains.compose.web.dom.Text(" This one cost me three iterations. The thing taking the checkpoint warms the application up over HTTP, and HTTP keep-alive is doing its job: the server's ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("accepted")
        }
        org.jetbrains.compose.web.dom.Text(" end of the warm-up connection is an open socket like any other.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""BusySelectorException: Selector has registered keys from channels:
  [SocketChannel[connected local=/127.0.0.1:18090 remote=/127.0.0.1:43872]]
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Closing the workload's client was not enough; the readiness probe held one too. Nothing in CRaC's documentation warns about it, because that documentation is about the application's sockets, not about the sockets of whatever is driving it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-a-restore-freezes-besides-the-state") }) {
        org.jetbrains.compose.web.dom.Text("What a restore freezes, besides the state")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The path of every file the process will read again.")
        }
        org.jetbrains.compose.web.dom.Text(" A snapshot itself travels fine: taken into a mounted directory at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/out/crac")
        }
        org.jetbrains.compose.web.dom.Text(", it restores from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/opt/app/zavarnik/crac")
        }
        org.jetbrains.compose.web.dom.Text(" inside an image built as \"that image plus one layer\" — 46 ms to ready, route answers 200. What does not travel is the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("policy file")
        }
        org.jetbrains.compose.web.dom.Text(": the JVM records ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jdk.crac.resource-policies")
        }
        org.jetbrains.compose.web.dom.Text(" as the string it was given and reads the file again on restore.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""warp: Restore successful!
OpenResourcePolicies${'$'}ConfigurationException:
  File /out/crac/zavarnik-crac-policies.yaml used in property jdk.crac.resource-policies does not exist
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Read those two lines together. The engine reports success, the JVM then throws, and on a stand with the same directory mounted it never happens at all — it happens to whoever first runs the image somewhere else. Anything the restored process re-reads has to live inside the image.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The configuration read at startup.")
        }
        org.jetbrains.compose.web.dom.Text(" The restored container's environment does not reach it. konekt takes its brand from an environment variable at boot; a restored container started with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("BRAND=brand-b")
        }
        org.jetbrains.compose.web.dom.Text(" answers with brand-a's name, while a plain start of the same image answers with brand-b's. So a snapshot belongs to one combination of environment, and anything read once at boot — a brand, a database URL, a secret — is frozen into it. Spring's documentation puts the security half plainly: assume any sensitive data the JVM has seen ends up in the files.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The random number generators, in a way that is worse than it looks.")
        }
        org.jetbrains.compose.web.dom.Text(" Restore one snapshot twice and compare:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Generator")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Same in both replicas?")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Random")
                    }
                    org.jetbrains.compose.web.dom.Text(" constructed before the checkpoint")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("yes")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ThreadLocalRandom")
                    }
                    org.jetbrains.compose.web.dom.Text(" on a thread that predates the checkpoint")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("yes")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ThreadLocalRandom")
                    }
                    org.jetbrains.compose.web.dom.Text(" on a thread created ")
                    org.jetbrains.compose.web.dom.Em {
                        org.jetbrains.compose.web.dom.Text("after")
                    }
                    org.jetbrains.compose.web.dom.Text(" the restore")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("yes")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("SplittableRandom")
                    }
                    org.jetbrains.compose.web.dom.Text(", before the checkpoint or after the restore")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("yes")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Math.random()")
                    }
                    org.jetbrains.compose.web.dom.Text(", once anything called it before the checkpoint")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("yes")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("new Random()")
                    }
                    org.jetbrains.compose.web.dom.Text(" constructed after the restore")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("no")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("RandomGenerator.getDefault()")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("UUID.randomUUID()")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("no")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("SecureRandom()")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("no — the JDK reseeds it")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("SecureRandom(byte[] seed)")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("no — documented as ")
                    org.jetbrains.compose.web.dom.Em {
                        org.jetbrains.compose.web.dom.Text("not")
                    }
                    org.jetbrains.compose.web.dom.Text(" reseeded, but the Linux provider mixes the seed with system entropy")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The third row is the surprise, and the fourth is the same surprise for ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SplittableRandom")
        }
        org.jetbrains.compose.web.dom.Text(": making a fresh thread or a fresh instance does not help, because the seeder that initialises new ones is in the snapshot too. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Math.random()")
        }
        org.jetbrains.compose.web.dom.Text(" is one shared ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Random")
        }
        org.jetbrains.compose.web.dom.Text(" behind a static, and in a running service something has already touched it. And that third row is the one Kotlin code lands on: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlin.random.Random.Default")
        }
        org.jetbrains.compose.web.dom.Text(" resolves, on a JDK 8 or newer, to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlin.random.jdk8.PlatformThreadLocalRandom")
        }
        org.jetbrains.compose.web.dom.Text(" backed by ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("java.util.concurrent.ThreadLocalRandom")
        }
        org.jetbrains.compose.web.dom.Text(" — asked of the running JVM through reflection rather than read off the source, because the platform implementation is chosen at runtime.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And yet konekt, whose eSIM mock draws its activation codes from exactly that, produced five different codes across five restores of one snapshot. The stream is shared; the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("consumption")
        }
        org.jetbrains.compose.web.dom.Text(" is not, because sign-in, token issuing, screens, Exposed and HikariCP all draw from it first, on whichever pool thread served the request. A collision is possible and not reproducible on demand — which also means a check that restores twice and compares the product's own output would be green for the wrong reason. Probe the generators, not the answers.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The image, down to its bytes.")
        }
        org.jetbrains.compose.web.dom.Text(" The same snapshot under Zulu 25.0.4 — one build older, same major — fails with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Cannot find build-id … validation failed")
        }
        org.jetbrains.compose.web.dom.Text(": the engine verifies every file the process had mapped, by the path it had at checkpoint.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-cpu-and-the-advice-that-makes-it-worse") }) {
        org.jetbrains.compose.web.dom.Text("The CPU, and the advice that makes it worse")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Across machines the hazard starts as a loud refusal, which is better than the AOT cache's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGILL")
        }
        org.jetbrains.compose.web.dom.Text(" — and stays better only until you follow the JVM's own advice about it. Measured on two machines, an AMD EPYC-Genoa with AVX-512 and an Intel Core Ultra 7 without it, one pinned image digest so the JDK is byte-identical on both:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("checkpoint on")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("flag")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("restore on")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("result")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wide")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("none")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("narrow")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("refused, exit 1, message names a mask")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wide")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("-XX:CPUFeatures=generic")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("narrow")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("SIGSEGV")
                    }
                    org.jetbrains.compose.web.dom.Text(", exit 139, no message at all")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("narrow")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("-XX:CPUFeatures=generic")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wide")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("restored")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("narrow")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("none")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wide")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("refused, exit 1")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wide")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("generic")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wide, itself")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("restored — the control that says the flag broke nothing")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two things in that table are worth more than the rest. The first: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("without the flag the constraint is an exact match, not a subset")
        }
        org.jetbrains.compose.web.dom.Text(" — narrow → wide refuses too, though every instruction the snapshot could want is present. The engine prints both bitmaps and calls it ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Image constraint 'cpu.features' (bitmap subset) does not match")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The second is the trap. On the refusal the JVM prints ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("try using -XX:CPUFeatures=0x… on checkpoint")
        }
        org.jetbrains.compose.web.dom.Text(". Do that, and on the machine that refused, the restore no longer refuses — it segfaults, with no JVM message and no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("hs_err")
        }
        org.jetbrains.compose.web.dom.Text(" file. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("generic")
        }
        org.jetbrains.compose.web.dom.Text(" behaves the same way, and so does the older CRIU engine: a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("generic")
        }
        org.jetbrains.compose.web.dom.Text(" snapshot taken with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:CRaCEngine=criuengine")
        }
        org.jetbrains.compose.web.dom.Text(" restores on the machine that took it and crashes the same silent way on the other — so this is not a property of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("warp")
        }
        org.jetbrains.compose.web.dom.Text(". The documented workaround converts a loud failure into a silent one, in the direction you are most likely to need it: from the larger build machine to the smaller production one.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The rule that does work:")
        }
        org.jetbrains.compose.web.dom.Text(" take the snapshot on the narrowest CPU it will ever be restored on, with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:CPUFeatures=generic")
        }
        org.jetbrains.compose.web.dom.Text(". What that costs, six restores per variant in alternation with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("oha")
        }
        org.jetbrains.compose.web.dom.Text(" at 32 connections for ten seconds behind each: readiness unchanged, 42 ms against 46; throughput lower in all six pairs, 5 526 against 4 089 requests a second at the median. The magnitude is not trustworthy — the ranges overlap and the box was not isolated — the direction is.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is also what rules the snapshot out of the pipeline that motivated it. konekt's release image is built on a hosted CI runner, so the machine that would take the checkpoint is whatever the provider hands out that day, and it changes between runs. Before \"which CPU does production have\" comes \"where can a checkpoint be taken on a CPU that is the same next week\".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-costs-to-have-at-all") }) {
        org.jetbrains.compose.web.dom.Text("What it costs to have at all")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A JDK with CRaC, and for JDK 25 that means Azul Zulu and nobody else — BellSoft's own page for Liberica with CRaC lists 17 and 21, and its container images carry the same two. Linux. A layer the size of the snapshot on every image — 143 MB on konekt, 82 on the sample. A snapshot per environment combination. Secrets inside that snapshot.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What it no longer costs is privileges. The CRIU engine wanted root, or ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CHECKPOINT_RESTORE")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SYS_PTRACE")
        }
        org.jetbrains.compose.web.dom.Text(" in a container; since October 2024 Azul ships ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("warp")
        }
        org.jetbrains.compose.web.dom.Text(", which needs neither, and on 25.0.4.1 it is the default. On my box ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--cap-add CHECKPOINT_RESTORE --cap-add SYS_PTRACE")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--privileged")
        }
        org.jetbrains.compose.web.dom.Text(" changed nothing, because nothing was needed.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-frameworks-are-and-where-they-are-not") }) {
        org.jetbrains.compose.web.dom.Text("Where the frameworks are, and where they are not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Spring Framework 6.1 and Boot 3.2 stop beans before the checkpoint and start them after, with a dedicated lifecycle for HikariCP. Micronaut suspends the Hikari pool, waits for its connections to close, and resumes it afterwards. Quarkus and Helidon 4.2 have their own support.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Underneath, the picture is thinner. HikariCP's own issue for this — \"Allow to stop/restart HikariPool\" — has been open since 2023, and its comments describe exactly why ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("close")
        }
        org.jetbrains.compose.web.dom.Text(" loses the race: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("suspend")
        }
        org.jetbrains.compose.web.dom.Text(" does not guarantee the connections are closed, and after ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("softEvictConnections")
        }
        org.jetbrains.compose.web.dom.Text(" the sockets linger for about half a second. Ktor's request, KTOR-6485, has been ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Submitted")
        }
        org.jetbrains.compose.web.dom.Text(" since November 2023 with three votes. Exposed and pgjdbc have no issue mentioning CRaC at all. A Netty pull request to close its epoll descriptors at checkpoint was closed without merging.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the frameworks did the half that is theirs — a lifecycle around the checkpoint — and on a Ktor plus Exposed stack there is neither a framework doing it nor a pool that can be told to. What fills the gap here is three rules in a policy file and a verification that actually talks to the database.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("doing-it") }) {
        org.jetbrains.compose.web.dom.Text("Doing it")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("By hand, in an image whose JDK has CRaC:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""java -XX:CRaCCheckpointTo=/snapshot -Djdk.crac.resource-policies=/app/policies.yaml -jar app.jar &
# wait until it is ready, drive the hot path, then:
jcmd <pid> JDK.checkpoint          # this ends the process; the snapshot is complete when it exits
java -XX:CRaCRestoreFrom=/snapshot # every other flag comes back from the snapshot
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The policy file for a service with a database and a broker:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-yaml") }) { org.jetbrains.compose.web.dom.Text("""type: SOCKET
listening: true
action: reopen
---
type: SOCKET
remotePort: 5432
action: ignore
---
type: SOCKET
remotePort: 9092
action: ignore
""") } }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" does this as build tasks, because the snapshot has to be taken by the JVM that will restore it, inside the image that will carry it. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("crac { ignoreRemotePort(5432, 9092) }")
        }
        org.jetbrains.compose.web.dom.Text(" is the only thing it cannot work out for itself. On Jib — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor { docker { } }")
        }
        org.jetbrains.compose.web.dom.Text(" included — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jibCracCheckpoint")
        }
        org.jetbrains.compose.web.dom.Text(" takes the snapshot in a container of the image just built, and the next build lays it over that image and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("replaces")
        }
        org.jetbrains.compose.web.dom.Text(" the entrypoint with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("java -XX:CRaCRestoreFrom=…")
        }
        org.jetbrains.compose.web.dom.Text(", because a restore carries neither classpath nor main class. An image therefore holds either an AOT cache or a snapshot, never both. That image answers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(" 58 ms after ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker run")
        }
        org.jetbrains.compose.web.dom.Text(" on a GitHub runner, which is where its check runs.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One stack, measured once: Ktor CIO, HikariCP 7.1, Exposed 1.5, pgjdbc 42.7, on Zulu 25.0.4.1. The ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ignore")
            }
            org.jetbrains.compose.web.dom.Text(" recipe rests on HikariCP validating on borrow; a pool that does not would need the suspend-and-wait dance Micronaut implements.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The memory row is not a saving. A restored process maps the snapshot lazily and its RSS stays low until pages are touched; a hundred requests touch some of them, not all. Read 36 / 62 MiB against 70 / 100 as \"different accounting\", not as \"CRaC uses less memory\".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Numbers from one box, five or ten restarts per variant in alternation, no CPU limit. The AOT-cache figures they invite comparison with were taken under one core; do not pair them.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The restore times are ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("docker run")
            }
            org.jetbrains.compose.web.dom.Text(" to the first ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("200")
            }
            org.jetbrains.compose.web.dom.Text(", from outside the container. What a Kubernetes rollout does with them depends on probes, and that is not measured here.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Netty is not covered: Ktor's CIO engine is what was tested, and the pull request that would have taught Netty to close its epoll descriptors was closed unmerged.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The CPU pair is one pair: an EPYC Genoa guest against a Core Ultra laptop part, both x86_64. The silent crash reproduces with both of Zulu's engines, so it is not the engine; whether it is this build or that specific feature gap is not established here — what is established is that it happens when you do what the JVM's message says.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Nothing here says a restore is ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("safe")
            }
            org.jetbrains.compose.web.dom.Text(" for a given application. It says what refuses to checkpoint and what comes back working; whether a frozen configuration and a shared random stream are acceptable is a property of the service, not of CRaC.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("experiments/crac-smoke/")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("experiments/crac-ktor/")
        }
        org.jetbrains.compose.web.dom.Text(" for the scripts and every log quoted, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/research-crac.md")
        }
        org.jetbrains.compose.web.dom.Text(" for the fact tables with their addresses; ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/konekt") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/konekt")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/measurements-2026-09-11/crac/")
        }
        org.jetbrains.compose.web.dom.Text(", for the service and the ten hypotheses it was put through. The AOT cache, which is the other half of this story, has its own how-to: ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/leyden-aot-cache-for-a-plain-kotlin-jvm-service") {
            org.jetbrains.compose.web.dom.Text("Leyden AOT cache for a plain Kotlin/JVM service")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
